import { useMemo, useRef, useEffect } from "react";

/**
 * Ternary Diagram with per-pixel scent influence coloring.
 *
 * Each vertex (Top, Heart, Base) holds an array of scents with weights
 * (ingredient percentages). For every coordinate inside the triangle the
 * influence of each scent is calculated, then blended to color the pixel.
 */

// Stable deterministic scent color
function hashString(str) {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  return Math.abs(hash);
}

function getScentColor(name) {
  const hue = hashString(name) % 360;
  return `hsl(${hue}, 65%, 55%)`;
}

function hslToRgb(hsl) {
  const [h, s, l] = hsl.match(/\d+/g).map(Number);
  const sN = s / 100;
  const lN = l / 100;
  const k = n => (n + h / 30) % 12;
  const a = sN * Math.min(lN, 1 - lN);
  const f = n =>
    lN - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
  return [255 * f(0), 255 * f(8), 255 * f(4)];
}

export default function TernaryDiagram({ ingredients, materials = [] }) {
  const canvasRef = useRef(null);

  const width = 380;
  const height = 330;
  const padding = 50;

  const vertices = useMemo(() => {
    const triH = height - padding * 2;
    const triW = (triH * 2) / Math.sqrt(3);
    return {
      top: { x: width / 2, y: padding },
      heart: { x: width / 2 - triW / 2, y: height - padding },
      base: { x: width / 2 + triW / 2, y: height - padding },
    };
  }, [width, height, padding]);

  const toCartesian = (t, h, b) => ({
    x: vertices.top.x * t + vertices.heart.x * h + vertices.base.x * b,
    y: vertices.top.y * t + vertices.heart.y * h + vertices.base.y * b,
  });

  const compositionData = useMemo(() => {
    const noteTypes = { top: [], heart: [], base: [] };

    ingredients.forEach((ing) => {
      const nt = ing.note_type || "unclassified";
      if (nt === "top" || nt === "heart" || nt === "base") {
        const mat = materials.find((m) => m.material_id === ing.material_id);
        const odorImpact = mat?.odor_impact || ing.odor_impact || 500;
        noteTypes[nt].push({
          name: ing.material_name,
          percentage: parseFloat(ing.percentage) || 0,
          odorImpact,
        });
      }
    });

    const calc = (notes) => {
      if (!notes.length) return { percentage: 0, avgImpact: 500, power: 0 };
      const totalPct = notes.reduce((s, n) => s + n.percentage, 0);
      const wImpact = notes.reduce((s, n) => s + n.odorImpact * n.percentage, 0);
      const avgImpact = totalPct > 0 ? wImpact / totalPct : 500;
      return { percentage: totalPct, avgImpact, power: (totalPct * avgImpact) / 100 };
    };

    const stats = { top: calc(noteTypes.top), heart: calc(noteTypes.heart), base: calc(noteTypes.base) };
    const totalPct = stats.top.percentage + stats.heart.percentage + stats.base.percentage;
    const normalized = {
      top: totalPct > 0 ? stats.top.percentage / totalPct : 0.33,
      heart: totalPct > 0 ? stats.heart.percentage / totalPct : 0.33,
      base: totalPct > 0 ? stats.base.percentage / totalPct : 0.34,
    };

    let dominant = null;
    if (stats.top.power + stats.heart.power + stats.base.power > 0) {
      if (stats.top.power >= stats.heart.power && stats.top.power >= stats.base.power) dominant = "top";
      else if (stats.heart.power >= stats.top.power && stats.heart.power >= stats.base.power) dominant = "heart";
      else dominant = "base";
    }

    return { stats, normalized, totalPct, dominant };
  }, [ingredients, materials]);

  const scentData = useMemo(() => {
    const vScents = { top: {}, heart: {}, base: {} };

    ingredients.forEach((ing) => {
      const nt = ing.note_type;
      if (nt === "top" || nt === "heart" || nt === "base") {
        const name = ing.material_name;
        const w = parseFloat(ing.percentage) || 0;
        vScents[nt][name] = (vScents[nt][name] || 0) + w;
      }
    });

    const allNames = new Set();
    Object.values(vScents).forEach(obj =>
      Object.keys(obj).forEach(n => allNames.add(n))
    );

    const scentList = [...allNames];

    const colorMap = {};
    const rgbMap = {};

    scentList.forEach(name => {
      const hsl = getScentColor(name);
      colorMap[name] = hsl;
      rgbMap[name] = hslToRgb(hsl);
    });

    return { scentList, colorMap, rgbMap, vScents };
  }, [ingredients]);

  const hasIngredients = compositionData.totalPct > 0;

  // ─── canvas: multi-scent pixel-level coloring ────────────────
  // ─── canvas: multi-scent pixel-level coloring with note borders ────────────────
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");

    if (!hasIngredients || scentData.scentList.length === 0) {
      ctx.clearRect(0, 0, width, height);
      return;
    }

    const { scentList, rgbMap, vScents } = scentData;
    const { top: v0, heart: v1, base: v2 } = vertices;
    const det = (v1.y - v2.y) * (v0.x - v2.x) + (v2.x - v1.x) * (v0.y - v2.y);

    const len = scentList.length;
    const tw = new Float64Array(len);
    const hw = new Float64Array(len);
    const bw = new Float64Array(len);
    for (let i = 0; i < len; i++) {
      tw[i] = vScents.top[scentList[i]] || 0;
      hw[i] = vScents.heart[scentList[i]] || 0;
      bw[i] = vScents.base[scentList[i]] || 0;
    }
    const rgbArr = scentList.map(n => rgbMap[n]);

    const imageData = ctx.createImageData(width, height);
    const px = imageData.data;

    const minX = Math.floor(Math.min(v0.x, v1.x, v2.x)) - 1;
    const maxX = Math.ceil(Math.max(v0.x, v1.x, v2.x)) + 1;
    const minY = Math.floor(Math.min(v0.y, v1.y, v2.y)) - 1;
    const maxY = Math.ceil(Math.max(v0.y, v1.y, v2.y)) + 1;

    const borderThreshold = 0.005; // smaller = thinner border

    // Inside your canvas drawing useEffect
    for (let y = minY; y <= maxY; y++) {
      for (let x = minX; x <= maxX; x++) {
        const a = ((v1.y - v2.y) * (x - v2.x) + (v2.x - v1.x) * (y - v2.y)) / det;
        const bc = ((v2.y - v0.y) * (x - v2.x) + (v0.x - v2.x) * (y - v2.y)) / det;
        const cc = 1 - a - bc;

        if (a >= -0.002 && bc >= -0.002 && cc >= -0.002) {
          const tR = Math.max(0, a);
          const hR = Math.max(0, bc);
          const bR = Math.max(0, cc);

          // Determine which note dominates this pixel
          const ratios = [tR, hR, bR];
          const maxRatio = Math.max(...ratios);
          const noteIndex = ratios.indexOf(maxRatio); // 0 = top, 1 = heart, 2 = base
          const noteKeys = ["top", "heart", "base"];
          const note = noteKeys[noteIndex];

          // Blend colors of all ingredients in that note
          const ingredientsInNote = Object.keys(vScents[note]);
          let r = 0, g = 0, b = 0, total = 0;
          ingredientsInNote.forEach((ingName) => {
            const weight = vScents[note][ingName];
            const rgb = rgbMap[ingName];
            r += rgb[0] * weight;
            g += rgb[1] * weight;
            b += rgb[2] * weight;
            total += weight;
          });
          if (total > 0) {
            r /= total; g /= total; b /= total;
          }

          // Border check (if ratios are very close, make pixel black)
          const borderThreshold = 0.005;
          const nearBorder = ratios.filter(r => Math.abs(r - maxRatio) < borderThreshold).length > 1;

          const idx = (y * width + x) * 4;
          if (nearBorder) {
            px[idx] = 0;
            px[idx + 1] = 0;
            px[idx + 2] = 0;
            px[idx + 3] = 255;
          } else {
            px[idx] = r;
            px[idx + 1] = g;
            px[idx + 2] = b;
            px[idx + 3] = 170;
          }
        }
      }
    }

    ctx.clearRect(0, 0, width, height);
    ctx.putImageData(imageData, 0, 0);
  }, [scentData, vertices, hasIngredients, width, height]);
  const pos = toCartesian(
    compositionData.normalized.top,
    compositionData.normalized.heart,
    compositionData.normalized.base
  );

  const { stats, dominant } = compositionData;

  const noteColor = { top: "#60a5fa", heart: "#c084fc", base: "#f59e0b" };

  return (
    <div className="bg-noir-paper border border-zinc-800 p-4" data-testid="ternary-diagram">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-white font-medium">Composition Ternary</h3>
        {dominant && (
          <span
            data-testid="dominant-note-badge"
            className={`text-xs px-2 py-1 rounded ${dominant === "top" ? "bg-blue-500/20 text-blue-400" :
              dominant === "heart" ? "bg-purple-500/20 text-purple-400" :
                "bg-amber-500/20 text-amber-500"
              }`}
          >
            {dominant.charAt(0).toUpperCase() + dominant.slice(1)} Dominant
          </span>
        )}
      </div>

      {/* Diagram */}
      <div className="flex justify-center">
        <div className="relative" style={{ width, height }}>
          <canvas
            ref={canvasRef}
            width={width}
            height={height}
            className="absolute inset-0"
            data-testid="ternary-canvas"
          />
          <svg
            width={width}
            height={height}
            className="absolute inset-0 overflow-visible"
          >
            {/* Triangle outline */}
            <polygon
              points={`${vertices.top.x},${vertices.top.y} ${vertices.heart.x},${vertices.heart.y} ${vertices.base.x},${vertices.base.y}`}
              fill="transparent"
              stroke="#52525b"
              strokeWidth="1.5"
            />

            {/* Grid lines */}
            <g stroke="#3f3f46" strokeWidth="0.4" opacity="0.45">
              {[0.25, 0.5, 0.75].map((p, i) => {
                const a = toCartesian(p, 1 - p, 0), b = toCartesian(p, 0, 1 - p);
                return <line key={`t${i}`} x1={a.x} y1={a.y} x2={b.x} y2={b.y} />;
              })}
              {[0.25, 0.5, 0.75].map((p, i) => {
                const a = toCartesian(1 - p, p, 0), b = toCartesian(0, p, 1 - p);
                return <line key={`h${i}`} x1={a.x} y1={a.y} x2={b.x} y2={b.y} />;
              })}
              {[0.25, 0.5, 0.75].map((p, i) => {
                const a = toCartesian(1 - p, 0, p), b = toCartesian(0, 1 - p, p);
                return <line key={`b${i}`} x1={a.x} y1={a.y} x2={b.x} y2={b.y} />;
              })}
            </g>

            {/* Ingredient dots */}
            {ingredients.map((ing, i) => {
              const nt = ing.note_type;
              if (!["top", "heart", "base"].includes(nt)) return null;

              // For single-ingredient positioning:
              let t = 0, h = 0, b = 0;
              if (nt === "top") t = 1;
              else if (nt === "heart") h = 1;
              else b = 1;

              // Optional: weight by ingredient percentage to shift toward center
              const weight = parseFloat(ing.percentage) || 0;
              const shift = weight / 100; // scale factor (0-1)
              // Move slightly toward center (0.33,0.33,0.34)
              t = t * (1 - shift) + 0.33 * shift;
              h = h * (1 - shift) + 0.33 * shift;
              b = b * (1 - shift) + 0.34 * shift;

              const pos = toCartesian(t, h, b);
              return (
                <circle
                  key={i}
                  cx={pos.x}
                  cy={pos.y}
                  r={3} // dot size
                  fill={scentData.colorMap[ing.material_name] || "#fff"}
                  stroke="#000"
                  strokeWidth={0.5}
                />
              );
            })}

            {/* Vertex labels */}
            <g fontSize="12" fontWeight="500">
              <text x={vertices.top.x} y={vertices.top.y - 14} textAnchor="middle" fill={noteColor.top}>
                Top Notes
              </text>
              <text x={vertices.heart.x - 8} y={vertices.heart.y + 20} textAnchor="end" fill={noteColor.heart}>
                Heart Notes
              </text>
              <text x={vertices.base.x + 8} y={vertices.base.y + 20} textAnchor="start" fill={noteColor.base}>
                Base Notes
              </text>
            </g>
          </svg>
        </div>
      </div>

      {/* Scent legend */}
      {hasIngredients && scentData.scentList.length > 0 && (
        <div className="mt-4 pt-3 border-t border-zinc-800" data-testid="ternary-legend">
          <p className="text-zinc-500 text-[10px] font-medium mb-2 uppercase tracking-wider">
            Scent Influence Map
          </p>
          <div className="flex flex-wrap gap-x-4 gap-y-1.5">
            {scentData.scentList.map(name => (
              <div key={name} className="flex items-center gap-1.5" data-testid={`legend-${name}`}>
                <span
                  className="w-2.5 h-2.5 rounded-sm inline-block flex-shrink-0"
                  style={{ backgroundColor: scentData.colorMap[name] }}
                />
                <span className="text-xs text-zinc-400 truncate max-w-[140px]">{name}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}